import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Users, BarChart2, ArrowUpRight, Clock, CheckCircle, XCircle, AlertCircle, Zap } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const API = 'https://api.naijapredict.net';

async function apiFetch(path) {
  const token = localStorage.getItem('creator_token');
  const res = await fetch(API + path, { headers: { Authorization: 'Bearer ' + token } });
  return res.json();
}

function StatCard({ icon: Icon, label, value, sub, color='#00c566', trend }) {
  return (
    <div className="card" style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ width:40, height:40, borderRadius:10, background:`rgba(${color==='#00c566'?'0,197,102':color==='#f5c842'?'245,200,66':'255,107,53'},.08)`, border:`1px solid rgba(${color==='#00c566'?'0,197,102':color==='#f5c842'?'245,200,66':'255,107,53'},.15)`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Icon size={18} color={color} strokeWidth={2}/>
        </div>
        {trend !== undefined && (
          <div style={{ fontSize:12, color: trend>=0?'#00c566':'#ff4444', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
            <ArrowUpRight size={12} style={{ transform: trend<0?'rotate(90deg)':'' }}/>{Math.abs(trend)}%
          </div>
        )}
      </div>
      <div>
        <div style={{ fontSize:28, fontWeight:800, fontFamily:"'Syne',sans-serif", color:'#fff' }}>{value}</div>
        <div style={{ fontSize:13, color:'#4a5a63', marginTop:2 }}>{label}</div>
        {sub && <div style={{ fontSize:11, color:'#00c566', marginTop:4 }}>{sub}</div>}
      </div>
    </div>
  );
}

function MarketRow({ market }) {
  const stateColors = { 0:'#00c566', 1:'#f5c842', 2:'#ff4444' };
  const stateLabels = { 0:'Active', 1:'Resolved', 2:'Cancelled' };
  const StateIcon = { 0:Clock, 1:CheckCircle, 2:XCircle }[market.state] || AlertCircle;

  return (
    <div style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:14, fontWeight:600, color:'#e8eaeb', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{market.question}</div>
        <div style={{ display:'flex', gap:12, fontSize:12, color:'#4a5a63' }}>
          <span>{market.category}</span>
          <span>YES {market.yesPct}% / NO {market.noPct}%</span>
        </div>
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>${parseFloat(market.totalCollateral||0).toFixed(2)}</div>
        <div style={{ fontSize:11, color:'#4a5a63' }}>pool</div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:stateColors[market.state], fontWeight:600, flexShrink:0 }}>
        <StateIcon size={13} strokeWidth={2}/>{stateLabels[market.state]}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats]   = useState(null);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/creator/stats'),
      apiFetch('/api/creator/markets'),
    ]).then(([s, m]) => {
      setStats(s);
      setMarkets(m.slice(0,5));
    }).finally(() => setLoading(false));
  }, []);

  // Placeholder chart data
  const chartData = Array.from({length:14}, (_,i) => ({
    day: `Day ${i+1}`,
    volume: Math.floor(Math.random()*500+100),
    earnings: Math.floor(Math.random()*25+5),
  }));

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300 }}>
      <div style={{ color:'#00c566' }}>Loading dashboard...</div>
    </div>
  );

  return (
    <div>
      {/* Beta Banner */}
      <div style={{ background:'rgba(245,200,66,.06)', border:'1px solid rgba(245,200,66,.15)', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <AlertCircle size={16} color="#f5c842" strokeWidth={2} style={{ flexShrink:0 }}/>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'#f5c842' }}>Creator Portal - Beta</div>
            <div style={{ fontSize:12, color:'#4a5a63' }}>Analytics show placeholder data. Live data coming with mainnet launch.</div>
          </div>
        </div>
        <a href="https://naijapredict.net" target="_blank" rel="noreferrer" style={{ fontSize:12, color:'#00c566', fontWeight:600, whiteSpace:'nowrap', textDecoration:'none' }}>Visit App</a>
      </div>

      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:28, fontWeight:800, fontFamily:"'Syne',sans-serif", marginBottom:4 }}>Dashboard</h1>
        <p style={{ fontSize:14, color:'#4a5a63' }}>Overview of your creator performance and earnings</p>
      </div>

      {/* Stats Grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:24 }}>
        <StatCard icon={BarChart2}  label="Total Markets"    value={stats?.totalMarkets||0}                                          color="#00c566" trend={12}/>
        <StatCard icon={DollarSign} label="Total Volume"     value={`$${parseFloat(stats?.totalVolume||0).toFixed(2)}`}              color="#f5c842" trend={8}/>
        <StatCard icon={Zap}        label="Royalties Earned" value={`$${parseFloat(stats?.totalEarnings||0).toFixed(4)}`}            color="#00c566" sub="0.5% of volume"/>
        <StatCard icon={Users}      label="Active Traders"   value={stats?.totalTraders||0}                                          color="#ff6b35" trend={5}/>
      </div>

      {/* Charts Row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, marginBottom:24 }}>
        {/* Volume Chart */}
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <div>
              <div style={{ fontSize:16, fontWeight:700, marginBottom:2 }}>Trading Volume</div>
              <div style={{ fontSize:12, color:'#4a5a63' }}>Last 14 days</div>
            </div>
            <div style={{ fontSize:13, color:'#00c566', fontWeight:600 }}>+8% vs last week</div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#00c566" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#00c566" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} interval={3}/>
              <YAxis tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} width={35}/>
              <Tooltip contentStyle={{background:'#0d1114',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,fontSize:12}} labelStyle={{color:'#4a5a63'}} itemStyle={{color:'#00c566'}}/>
              <Area type="monotone" dataKey="volume" stroke="#00c566" strokeWidth={2} fill="url(#volGrad)" dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Earnings Chart */}
        <div className="card">
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:2 }}>Daily Earnings</div>
            <div style={{ fontSize:12, color:'#4a5a63' }}>Royalties (USDC)</div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <XAxis dataKey="day" tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} interval={3}/>
              <YAxis tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} width={30}/>
              <Tooltip contentStyle={{background:'#0d1114',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,fontSize:12}} labelStyle={{color:'#4a5a63'}} itemStyle={{color:'#f5c842'}}/>
              <Line type="monotone" dataKey="earnings" stroke="#f5c842" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Markets */}
      <div className="card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontSize:16, fontWeight:700 }}>Recent Markets</div>
          <a href="/markets" style={{ fontSize:13, color:'#00c566', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
            View all <ArrowUpRight size={13}/>
          </a>
        </div>
        {markets.length === 0 ? (
          <div style={{ textAlign:'center', padding:'32px 0', color:'#4a5a63' }}>
            <BarChart2 size={32} style={{ marginBottom:8, opacity:.5 }}/>
            <div style={{ fontSize:14 }}>No markets yet</div>
            <div style={{ fontSize:12, marginTop:4 }}>Create your first market to start earning</div>
          </div>
        ) : markets.map(m => <MarketRow key={m.id} market={m}/>)}
      </div>
    </div>
  );
}
